import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
} from "@mui/material";
import { useField } from "formik";

import { useGetUtilityLocales } from "hooks/useGetUtilityLocales";
import { LOCALE_DEFAULT } from "../../../constants/constants";

type LocaleSelectProps = SelectProps & {
  name: string;
};

const localeKey = (locale: string) => locale.replace(/_/g, "-").toLowerCase();

const matchingLocale = (value: string, locales: Array<{ value: string }>) =>
  locales.find((locale) => localeKey(locale.value) === localeKey(value))?.value;

export const LocaleSelect = ({
  name,
  ...props
}: LocaleSelectProps): JSX.Element => {
  const [{ value, onBlur }, { error, touched }, { setValue }] = useField(name);
  const { t } = useTranslation();
  const { locales, isLoading } = useGetUtilityLocales();
  const hasError = useMemo(() => touched && Boolean(error), [touched, error]);
  const onChange = useCallback(
    ({ target }) => {
      setValue(target.value);
    },
    [setValue]
  );
  const selectValue = useMemo(() => {
    const current = typeof value === "string" && value ? value : LOCALE_DEFAULT;
    return matchingLocale(current, locales) ?? current;
  }, [locales, value]);
  const options = useMemo(() => {
    if (!selectValue || locales.some((locale) => locale.value === selectValue)) {
      return locales;
    }
    return [...locales, { name: selectValue, value: selectValue }];
  }, [locales, selectValue]);

  return (
    <FormControl error={hasError} size={props.size} fullWidth={props.fullWidth}>
      <InputLabel>{props.label}</InputLabel>
      <Select
        {...props}
        label={props.label}
        value={selectValue}
        onBlur={onBlur}
        displayEmpty
        disabled={props.disabled || isLoading}
        onChange={onChange}
        renderValue={
          isLoading
            ? () => (
                <>
                  <CircularProgress size={16} sx={{ marginRight: 2 }} />
                  {t("Loading")}
                </>
              )
            : undefined
        }
      >
        {options.map((locale) => (
          <MenuItem key={locale.value} value={locale.value}>
            {locale.name}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText error>{hasError ? error : " "}</FormHelperText>
    </FormControl>
  );
};
